import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from '@/features/auth/context/AuthContext';
import api from '@/utils/api.utils';

type ThemeMode = 'Light' | 'Dark';

interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, updateUser } = useAuth();

  const isMountingRef = useRef(true);
  const lastAppliedThemeRef = useRef<ThemeMode | null>(null);
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const savedTheme = localStorage.getItem('theme') as ThemeMode | null;
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (systemPrefersDark ? 'Dark' : 'Light');
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.setAttribute('data-theme', initialTheme.toLowerCase());
      root.style.colorScheme = initialTheme.toLowerCase();
      lastAppliedThemeRef.current = initialTheme;
    }

    return initialTheme;
  });

  useEffect(() => {
    if (user?.theme_preference) {
      const userTheme = user.theme_preference as ThemeMode;

      if (userTheme !== theme) {
        setThemeState(userTheme);
        applyTheme(userTheme);
      }
    }
  }, [user?.theme_preference]);

  useEffect(() => {
    if (isMountingRef.current) {
      isMountingRef.current = false;
      return;
    }

    applyTheme(theme);
  }, [theme]);

  const applyTheme = (newTheme: ThemeMode) => {
    if (lastAppliedThemeRef.current === newTheme) {
      return;
    }

    const root = document.documentElement;
    const currentTheme = root.getAttribute('data-theme');
    if (currentTheme !== newTheme.toLowerCase()) {
      root.setAttribute('data-theme', newTheme.toLowerCase());
      root.style.colorScheme = newTheme.toLowerCase();
    }

    const savedTheme = localStorage.getItem('theme');
    if (savedTheme !== newTheme) {
      localStorage.setItem('theme', newTheme);
    }

    lastAppliedThemeRef.current = newTheme;
  };

  const setTheme = async (newTheme: ThemeMode) => {
    setThemeState(newTheme);

    if (user) {
      try {
        const response = await api.patch('/api/v1/auth/me', {
          theme_preference: newTheme,
        });

        if (response.data) {
          updateUser({ ...user, theme_preference: newTheme });
        }
      } catch (error) {
        console.error('Failed to update theme preference:', error);
      }
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'Light' ? 'Dark' : 'Light';
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
